"use client";

import { WorkplaceRequestReservationFieldConfig } from "@gettactic/api";
import { CalendarInput } from "@gettactic/components/src/form/calendar/CalendarInput/CalendarInput";
import { FormControlWrapper } from "@gettactic/components/src/form/FormControlWrapper";
import { TimeSelect } from "@gettactic/components/src/form/TimeSelect";
import { UnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { Button } from "@gettactic/components/src/primitives/button";
import { EVERY_RESERVATION_PART } from "@gettactic/helpers/src/workplace-requests/reservationField";
import {
  EMPTY_RESERVATION_FORM_VALUE,
  isReservationFieldFormValue,
  type ReservationFieldFormValue,
  reservationFieldIssues,
  reservationValueAllowedBy
} from "@gettactic/helpers/src/workplace-requests/reservationFieldValue";
import { addDays } from "date-fns";
import { Clock, Trash2 } from "lucide-react";
import React, { useState } from "react";
import {
  Controller,
  type FieldValues,
  type UseFormReturn
} from "react-hook-form";
import {
  type AdvancedFormState,
  ReserveFieldRecurringAdvancedForm
} from "@/features/reserve/components/ReserveFieldRecurringAdvancedForm";
import {
  buildCustomReservationRRule,
  buildReservationRRule,
  RESERVATION_REPEATS,
  type ReservationRepeat,
  reanchorReservationRRule,
  reservationRepeatLabel,
  reservationRepeatOf,
  reservationRRuleOptions
} from "@/features/workplace-requests/lib/reservationRecurrence";
import { ReservationAttendeesInput } from "./ReservationAttendeesInput";
import {
  ReservationResourceAvailability,
  type ReservationResourceContext
} from "./ReservationResourceAvailability";

const NO_REPEAT = "none";
/** A rule edited by hand — or one this form didn't write and can't name. */
const CUSTOM_REPEAT = "custom";

/** The days in order, so "the first day" always means the earliest one. */
const sortedDates = (dates: Date[]): Date[] =>
  [...dates].sort((left, right) => left.getTime() - right.getTime());

interface ReservationRepeatFieldProps {
  value: ReservationFieldFormValue;
  onChange: (recurrence: ReservationFieldFormValue["recurrence"]) => void;
}

/**
 * How often the booking repeats, and when the series stops.
 *
 * The end date doubles as its own on/off switch: no date means a series with
 * no end, which is what core reads an unset `end_date` as.
 *
 * "Custom" hands the rule to the reserve flow's editor — frequency, interval,
 * weekdays and the end date in one block — so a request can ask for the same
 * repeats a direct booking can. The rule is kept apart from the end date the
 * whole way through: the editor's "Ends" is written to `endDate`, never into
 * the rule as UNTIL, which is how core reads the two.
 */
const ReservationRepeatField: React.FC<ReservationRepeatFieldProps> = ({
  value,
  onChange
}) => {
  const firstDate = sortedDates(value.dates)[0];
  const { recurrence } = value;
  const repeat = recurrence ? reservationRepeatOf(recurrence.rrule) : null;

  // Picking "Custom" has to stick even while the edited rule still equals a
  // preset, or the editor would vanish under the requester's first click.
  const [isCustomPicked, setIsCustomPicked] = useState(false);
  const [isCustomExpanded, setIsCustomExpanded] = useState(true);
  const isCustom = !!recurrence && (isCustomPicked || !repeat);

  const items = [
    { id: NO_REPEAT, name: "Does not repeat" },
    ...RESERVATION_REPEATS.map((option) => ({
      id: option,
      name: reservationRepeatLabel(option, firstDate)
    })),
    { id: CUSTOM_REPEAT, name: "Custom…" }
  ];

  const handleRepeatChange = (next: string) => {
    if (next === NO_REPEAT) {
      setIsCustomPicked(false);
      onChange(null);
      return;
    }
    if (next === CUSTOM_REPEAT) {
      setIsCustomPicked(true);
      setIsCustomExpanded(true);
      // Nothing to edit yet: open on a weekly repeat of the first day, which
      // is what the editor shows for a rule it can't read anyway.
      if (!recurrence) {
        onChange({
          rrule: buildReservationRRule("weekly", firstDate ?? new Date()),
          endDate: null
        });
      }
      return;
    }
    setIsCustomPicked(false);
    onChange({
      rrule: buildReservationRRule(
        next as ReservationRepeat,
        firstDate ?? new Date()
      ),
      endDate: recurrence?.endDate ?? null
    });
  };

  const customState: AdvancedFormState | null = recurrence
    ? {
        ...reservationRRuleOptions(recurrence.rrule),
        until: recurrence.endDate ?? null
      }
    : null;

  const handleCustomChange = (next: AdvancedFormState) => {
    onChange({
      rrule: buildCustomReservationRRule(next),
      endDate: next.until
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <UnifiedSelectInput
        items={items}
        value={
          recurrence
            ? isCustom
              ? CUSTOM_REPEAT
              : (repeat ?? CUSTOM_REPEAT)
            : NO_REPEAT
        }
        onChangeValue={handleRepeatChange}
      />
      {recurrence && isCustom && customState ? (
        <ReserveFieldRecurringAdvancedForm
          state={customState}
          setState={handleCustomChange}
          advanced={isCustomExpanded}
          setAdvanced={setIsCustomExpanded}
          isCustom
          // The end date is optional here as it is for a preset: a series with
          // no end is a valid answer, and the request is approved before it
          // books anything.
          showNeverOption
          minUntilDate={addDays(firstDate ?? new Date(), 1)}
        />
      ) : null}
      {recurrence && !isCustom ? (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <CalendarInput
              label="Repeat until"
              mode="single"
              variant="outlined"
              selectedDate={recurrence.endDate ?? undefined}
              setValueOnApply={false}
              onSelect={(date) =>
                onChange({
                  ...recurrence,
                  endDate: date instanceof Date ? date : null
                })
              }
            />
          </div>
          {recurrence.endDate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remove the end date"
              iconLeft={<Trash2 className="h-4 w-4" />}
              onClick={() => onChange({ ...recurrence, endDate: null })}
            >
              Clear
            </Button>
          ) : null}
        </div>
      ) : null}
      {recurrence ? (
        <p className="text-neutral-500 text-xs">
          {recurrence.endDate
            ? "Every occurrence up to that day is booked when the request is approved."
            : "Leave the end date empty for a series with no end."}
        </p>
      ) : null}
    </div>
  );
};

export interface ReservationFieldInputProps {
  form: UseFormReturn<FieldValues>;
  name: string;
  label: string;
  isRequired?: boolean;
  description?: string;
  /**
   * What this question collects — which of the controls below are shown.
   *
   * Omitted when the switches aren't known: a guest editing a shared request is
   * handed the request's field *values*, which carry no config at all. Reading
   * that as "collects nothing" would show one day of a booking that has three.
   */
  config?: WorkplaceRequestReservationFieldConfig;
  /**
   * Whether the org directory can be searched. Attendees are people, so with no
   * directory there is no picker and the list is left as it was answered.
   */
  canSearchUsers?: boolean;
  /**
   * The resource this request would book, when the form knows it. With it
   * the field shows the resource's day under the pickers, so a requester can
   * see a clash before filing. Nothing is checked without it.
   */
  resource?: ReservationResourceContext | null;
}

/**
 * Reservation form field: the days to book, the window they share, and — where
 * the question allows it — a repeat and the people to invite.
 *
 * The whole answer is one form value, so the controls are plain and the
 * `Controller` owns them: a change to the days re-anchors the repeat, which a
 * set of independently registered inputs could not do without an effect.
 *
 * Nothing is booked here. The answer sits on the request until it is approved,
 * and a `reservations:create` rule turns it into reservations then.
 */
export const ReservationFieldInput: React.FC<ReservationFieldInputProps> = ({
  form,
  name,
  label,
  isRequired = false,
  description,
  config = EVERY_RESERVATION_PART,
  canSearchUsers = true,
  resource = null
}) => (
  <Controller
    control={form.control}
    name={name}
    rules={{
      validate: (value) => {
        const issues = reservationFieldIssues(
          isReservationFieldFormValue(value)
            ? value
            : EMPTY_RESERVATION_FORM_VALUE,
          config,
          isRequired
        );
        return issues[0] ?? true;
      }
    }}
    render={({ field, fieldState }) => {
      const answer = isReservationFieldFormValue(field.value)
        ? field.value
        : EMPTY_RESERVATION_FORM_VALUE;

      // Shown reduced to what the question collects today: a switch turned off
      // after the request was filed leaves days, a repeat or guests behind, and
      // showing them in controls that no longer exist would ask the requester
      // to fix something they cannot see.
      const shown = reservationValueAllowedBy(answer, config);

      // Edits are written onto the whole answer, never onto the reduced view:
      // a part no control is showing is not this composer's to delete. Where it
      // really is disallowed `toReservationFieldValue` drops it on the way out;
      // where the control is merely missing — attendees with no directory to
      // pick them from — it travels on as it was answered.
      const patch = (next: Partial<ReservationFieldFormValue>) =>
        field.onChange({ ...answer, ...next });

      const handleDatesChange = (dates: Date[]) => {
        const next: ReservationFieldFormValue = { ...answer, dates };
        if (next.recurrence) {
          next.recurrence = {
            ...next.recurrence,
            rrule: reanchorReservationRRule(
              next.recurrence.rrule,
              sortedDates(dates)[0]
            )
          };
        }
        field.onChange(next);
      };

      return (
        <FormControlWrapper
          variant="unstyled"
          label={label}
          isRequired={isRequired}
          description={description}
          isInvalid={!!fieldState.error}
          error={fieldState.error?.message}
        >
          {/*
           * One bordered block, not a run of loose controls: the days, the
           * window, the repeat and the guests are one answer, and the border
           * is what says so on a form of a dozen other questions.
           */}
          {/*
           * `w-full`: the wrapper lays its content out in a row, so without
           * it the block shrinks to its controls instead of lining up with
           * the other questions of the form.
           */}
          <div className="flex w-full flex-col gap-3 rounded-lg border border-neutral-200 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {config.multi_day_enabled ? (
                /*
                 * The map reserve card's calendar: one day by default, a
                 * Multi-Day switch for several, and the picked days listed
                 * beside it until Apply. The list edits the picker's own
                 * state, which is why the answer is only written on Apply.
                 */
                <CalendarInput
                  mode="single"
                  variant="outlined"
                  allowMultipleSwitch
                  setValueOnApply
                  hideCancel
                  hideMultipleText
                  showDaysCount
                  selectedDate={
                    shown.dates.length > 1 ? shown.dates : shown.dates[0]
                  }
                  onSelect={(date) => {
                    if (Array.isArray(date)) return handleDatesChange(date);
                    if (date instanceof Date) return handleDatesChange([date]);
                  }}
                />
              ) : (
                <CalendarInput
                  mode="single"
                  variant="outlined"
                  selectedDate={shown.dates[0]}
                  // A single day lands straight on the answer: an Apply step
                  // asks the requester to confirm what they already clicked.
                  setValueOnApply={false}
                  onSelect={(date) =>
                    handleDatesChange(date instanceof Date ? [date] : [])
                  }
                />
              )}
              {/*
               * No labels: the clock says what these are, and their order says
               * which is which — the same bargain the calendar beside them
               * makes with its own icon.
               */}
              <TimeSelect
                variant="outlined"
                value={shown.startTime}
                onSelect={(startTime) => patch({ startTime })}
                placeholder="Start time"
                triggerProps={{
                  iconRight: <Clock className="h-4 w-4" />,
                  // `triggerProps` is spread after the trigger's own
                  // `className`, so this replaces it rather than adding to it:
                  // `w-full` has to be restated, and the size variant carries
                  // no horizontal padding of its own.
                  className: "w-full px-3"
                }}
              />
              <TimeSelect
                variant="outlined"
                value={shown.endTime}
                onSelect={(endTime) => patch({ endTime })}
                placeholder="End time"
                triggerProps={{
                  iconRight: <Clock className="h-4 w-4" />,
                  className: "w-full px-3"
                }}
              />
            </div>

            {/*
             * Awareness, not a gate: a clash shows here but the request can
             * still be filed, and the approver decides.
             */}
            {resource ? (
              <ReservationResourceAvailability
                resource={resource}
                dates={shown.dates}
                startTime={shown.startTime}
                endTime={shown.endTime}
                onChangeTimes={patch}
              />
            ) : null}

            {config.recurrence_enabled ? (
              <ReservationRepeatField
                value={shown}
                onChange={(recurrence) => patch({ recurrence })}
              />
            ) : null}

            {/*
             * No directory, no picker: attendees are chosen from the org's
             * people, which a guest cannot read. The list they were given
             * travels on untouched — `patch` writes onto the whole answer.
             */}
            {config.attendees_enabled && canSearchUsers ? (
              <ReservationAttendeesInput
                values={shown.attendees}
                onChange={(attendees) => patch({ attendees })}
              />
            ) : null}

            <p className="text-neutral-500 text-xs">
              Nothing is reserved until this request is approved.
            </p>
          </div>
        </FormControlWrapper>
      );
    }}
  />
);
